import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { bookAppointment } from "@/src/actions/scheduling";
import { toast } from "@/src/hooks/use-toast";
import { updateAppointmentsCache } from "@/src/lib/data-fetchers/appointments-fetchers";
import {
  GetSchedulingPractitionersErrors,
  GetSchedulingLocationsErrors,
  SchedulingError,
  SchedulingErrorDomain,
  createApiError,
} from "@/src/lib/data-fetchers/scheduling-fetchers-errors";
import { logger } from "@/src/lib/logging/logger";
import { Appointment, AppointmentCreatedBy } from "@/src/models/appointment";
import { AppointmentSlot, type AvailabilityCheckParams } from "@/src/models/comms/scheduling-schemas";
import { Patient } from "@/src/models/patient";
import { Practitioner } from "@/src/models/practitioner";
import { SchedulingRecommendation } from "@/src/models/scheduling-recommendation";
import {
  getReferralSchedulingRecommendation,
  verifyReferralSchedulingRecommendation,
  type VerifySchedulingRecommendationPayload,
} from "@/src/requests/referral";
import {
  getPatientAppointments,
  getPractitioners,
  getLocations,
  getSchedulingCapabilities,
  checkAvailability,
  isEstablishedPatient,
  type IsEstablishedPatientResponse,
} from "@/src/requests/scheduling";

export const SCHEDULING_CACHE_KEY = "scheduling";

const ESTABLISHED_PATIENT_ESTABLISHED_STALE_TIME_MS = 7 * 24 * 60 * 60 * 1000;
const ESTABLISHED_PATIENT_NEW_STALE_TIME_MS = 24 * 60 * 60 * 1000;

function getIsEstablishedPatientStaleTime(data: IsEstablishedPatientResponse | null | undefined): number {
  if (!data) {
    return 0;
  }

  // Established patients only fall out of status once attended visits age past the
  // 3-year lookback, so a longer cache is safe. New patients can flip to
  // established after a completed visit, so refresh more often.
  return data.isEstablishedPatient
    ? ESTABLISHED_PATIENT_ESTABLISHED_STALE_TIME_MS
    : ESTABLISHED_PATIENT_NEW_STALE_TIME_MS;
}

// Shared query key so callers that mutate the verification (the inline referral controls and the
// dedicated verification task) update the same cached recommendation, keeping the highlighted state
// consistent across views.
export const getReferralSchedulingRecommendationQueryKey = (referralId: string | null | undefined) =>
  ["referralSchedulingRecommendation", referralId] as const;

export function useReferralSchedulingRecommendation(referralId: string | null | undefined) {
  const query = useQuery({
    queryKey: getReferralSchedulingRecommendationQueryKey(referralId),
    queryFn: async () => {
      if (!referralId) {
        return null;
      }

      return await getReferralSchedulingRecommendation(referralId);
    },
    enabled: Boolean(referralId),
    staleTime: Infinity,
  });

  return {
    recommendation: query.data || null,
    isFetching: query.isFetching,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// Lets a reviewer mark the referral's scheduling recommendation correct/incorrect.
// The verified value gates whether the cron sends automated SMS outreach.
export function useVerifySchedulingRecommendation(referralId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: VerifySchedulingRecommendationPayload) => {
      if (!referralId) {
        throw new Error("Cannot verify scheduling recommendation without a referral id");
      }

      return verifyReferralSchedulingRecommendation(referralId, payload);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<SchedulingRecommendation | null>(
        getReferralSchedulingRecommendationQueryKey(referralId),
        updated
      );
    },
    onError: (error) => {
      logger.error("Error verifying scheduling recommendation", { referralId, error });

      toast({
        title: "Couldn't save verification",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function usePatientAppointments(patientId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [SCHEDULING_CACHE_KEY, "appointments", patientId],
    queryFn: async () => {
      if (!patientId) {
        return [] as Appointment[];
      }

      const appointments = await getPatientAppointments(patientId);

      updateAppointmentsCache(queryClient, appointments);

      return appointments;
    },
    enabled: Boolean(patientId),
    staleTime: Infinity,
  });

  return {
    appointments: query.data || [],
    isFetching: query.isFetching,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useIsEstablishedPatient(patientId?: string, options: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: [SCHEDULING_CACHE_KEY, "is-established-patient", patientId],
    queryFn: async () => {
      if (!patientId) {
        return null;
      }

      return isEstablishedPatient(patientId);
    },
    enabled: Boolean(patientId) && (options.enabled ?? true),
    staleTime: (query) => getIsEstablishedPatientStaleTime(query.state.data),
  });

  return {
    isEstablishedPatient: query.data?.isEstablishedPatient ?? null,
    isFetching: query.isFetching,
    isPending: query.isPending,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useSchedulingCapabilities(enabled = true) {
  const query = useQuery({
    queryKey: [SCHEDULING_CACHE_KEY, "capabilities"],
    queryFn: getSchedulingCapabilities,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    emrSchedulingSupported: query.data?.emrSchedulingSupported ?? false,
    locationBasedScheduling: query.data?.locationBasedScheduling ?? false,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function usePractitioners(patientId?: string, options: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: [SCHEDULING_CACHE_KEY, "practitioners", patientId],
    queryFn: async () => {
      if (!patientId) {
        return [] as Practitioner[];
      }

      try {
        const practitioners = await getPractitioners(patientId);

        if (practitioners.length === 0) {
          throw new SchedulingError(
            SchedulingErrorDomain.GET_SCHEDULING_PRACTITIONERS,
            GetSchedulingPractitionersErrors.NO_PROVIDERS
          );
        }

        return practitioners;
      } catch (error) {
        throw createApiError(error, SchedulingErrorDomain.GET_SCHEDULING_PRACTITIONERS);
      }
    },
    enabled: Boolean(patientId) && (options.enabled ?? true),
    staleTime: Infinity,
  });

  return {
    practitioners: query.data || [],
    isFetching: query.isFetching,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useLocations(patientId: string) {
  const query = useQuery({
    queryKey: [SCHEDULING_CACHE_KEY, "locations", patientId],
    queryFn: async () => {
      try {
        const locations = await getLocations(patientId);

        if (locations.length === 0) {
          throw new SchedulingError(
            SchedulingErrorDomain.GET_SCHEDULING_LOCATIONS,
            GetSchedulingLocationsErrors.NO_LOCATIONS
          );
        }

        return locations;
      } catch (error) {
        throw createApiError(error, SchedulingErrorDomain.GET_SCHEDULING_LOCATIONS);
      }
    },
    enabled: Boolean(patientId),
    staleTime: Infinity,
  });

  return {
    locations: query.data || [],
    isFetching: query.isFetching,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// Mutation so we can trigger it on button click
export function useAvailabilityCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patientId,
      appointmentType,
      practitionerIds,
      locationIds,
      scheduleAvailability,
    }: AvailabilityCheckParams & { patientId: string }) => {
      if (!patientId || practitionerIds.length === 0 || locationIds.length === 0 || !appointmentType) {
        return;
      }

      return checkAvailability(patientId, appointmentType, practitionerIds, locationIds, scheduleAvailability);
    },
    onSuccess: (appointmentSlots, variables) => {
      const queryKey = [
        SCHEDULING_CACHE_KEY,
        "availability",
        variables.patientId,
        variables.appointmentType,
        variables.practitionerIds.sort(),
        variables.locationIds.sort(),
        variables.scheduleAvailability,
      ];

      queryClient.setQueryData(queryKey, appointmentSlots);
    },
    onError: (error, variables) => {
      logger.error("Error checking availability", {
        patientId: variables.patientId,
        appointmentType: variables.appointmentType,
        error,
      });

      const apiError = createApiError(error, SchedulingErrorDomain.CHECK_AVAILABILITY);

      toast({
        title: "Error checking availability",
        description: apiError.message,
        variant: "destructive",
      });
    },
  });
}

export function useBookAppointmentMutation(
  createdBy: AppointmentCreatedBy.VALERIE_SMS | AppointmentCreatedBy.VALERIE_VOICE = AppointmentCreatedBy.VALERIE_SMS
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appointmentSlot, patient }: { appointmentSlot: AppointmentSlot; patient: Patient }) => {
      const result = await bookAppointment(appointmentSlot, patient, createdBy);

      if (!result) {
        throw new Error("Failed to book appointment");
      }

      return result;
    },
    onMutate: async ({ patient }) => {
      // Cancel any outgoing refetches for patient appointments
      await queryClient.cancelQueries({
        queryKey: [SCHEDULING_CACHE_KEY, "appointments", patient.id],
      });

      // Get the current appointments data
      const previousAppointments =
        queryClient.getQueryData<Appointment[]>([SCHEDULING_CACHE_KEY, "appointments", patient.id]) || [];

      return { previousAppointments };
    },
    onError: (error, variables, context) => {
      logger.error("Error booking appointment", {
        patientId: variables.patient.id,
        appointmentSlotId: variables.appointmentSlot.ehrId,
        error,
      });

      // Restore previous appointments data on error
      if (context) {
        queryClient.setQueryData(
          [SCHEDULING_CACHE_KEY, "appointments", variables.patient.id],
          context.previousAppointments
        );
      }

      const apiError = createApiError(error, SchedulingErrorDomain.BOOK_APPOINTMENT);

      toast({
        title: "Error booking appointment",
        description: apiError.message,
        variant: "destructive",
      });
    },
    onSuccess: (appointment, variables) => {
      logger.info("Successfully booked appointment", {
        appointmentId: appointment.id,
        patientId: variables.patient.id,
      });

      // Update the appointments cache with the new appointment
      queryClient.setQueryData<Appointment[]>(
        [SCHEDULING_CACHE_KEY, "appointments", variables.patient.id],
        (oldAppointments = []) => {
          const existingIndex = oldAppointments.findIndex(
            (existing) => existing.ehrId === appointment.ehrId || existing.id === appointment.id
          );

          if (existingIndex >= 0) {
            // Update existing appointment
            return oldAppointments.map((existing, index) => (index === existingIndex ? appointment : existing));
          }

          // Add new appointment at the beginning
          return [appointment, ...oldAppointments];
        }
      );

      // Invalidate availability queries to refresh available slots
      queryClient.invalidateQueries({
        queryKey: [SCHEDULING_CACHE_KEY, "availability"],
      });
    },
  });
}
